/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file misc files - based on sentry
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */
import { __values } from "tslib";
import { isUndefined, isPlainObject, isString, isFunction, isNumber, isArray, isBoolean, isRegExp } from './is';
import { supportsURL } from './supports';
import { stringify } from '../vender/stringify';
import { md5 } from '../vender/md5';
/** 最大序列化长度 */
var MAX_SERIALIZE_KEYS_LENGTH = 40;
/** 序列化最大深度 */
var MAX_SERIALIZE_EXCEPTION_DEPTH = 3;
/** 异常序列化最大负载 */
var MAX_SERIALIZE_EXCEPTION_SIZE = 50 * 1024;
/**
 * 检测某个对象是否拥有某个属性
 * @param obj 待检测对象
 * @param key 待检测属性，支持 string | symbol | number 类型
 * @returns boolean
 */
export function hasKey(obj, key) {
    return Reflect.has(obj, key);
}
/**
 * 遍历对象或者数组，调用回调函数处理
 * @param obj 对象或者数组
 * @param callback 回调函数，接收两个参数，如果是对象第一个参数为 key，第二个参数为 value，如果是数组，第一个参数位索引，第二个参数为数组值
 */
export function each(obj, callback) {
    if (Array.isArray(obj)) {
        // 数组
        var len = obj.length;
        for (var i = 0; i < len; i++) {
            callback.call(null, i, obj[i]);
        }
    }
    else {
        for (var k in obj) {
            if (hasKey(obj, k)) {
                callback.call(null, k, obj[k]);
            }
        }
    }
}
/**
 * 生成 GUID(UUID4)，32位十六进制字符
 * @returns string
 */
export function uuid4() {
    var crypto = window.crypto || window['msCrypto'];
    if (!isUndefined(crypto) && crypto.getRandomValues) {
        // Use window.crypto API if available
        // eslint-disable-next-line no-undef
        var arr = new Uint16Array(8);
        crypto.getRandomValues(arr);
        // set 4 in byte 7
        arr[3] = (arr[3] & 0xfff) | 0x4000;
        // set 2 most significant bits of byte 9 to '10'
        arr[4] = (arr[4] & 0x3fff) | 0x8000;
        var pad = function (num) {
            var v = num.toString(16);
            while (v.length < 4) {
                v = '0' + v;
            }
            return v;
        };
        return (pad(arr[0]) +
            pad(arr[1]) +
            pad(arr[2]) +
            pad(arr[3]) +
            pad(arr[4]) +
            pad(arr[5]) +
            pad(arr[6]) +
            pad(arr[7]));
    }
    else {
        // http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/2117523#2117523
        return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}
/**
 * 将对象转化为URL查询字符串
 * @param params 参数对象
 * @returns string
 */
export function urlencode(params) {
    // 如果支持 URL 直接使用较新的 API
    if (supportsURL()) {
        return new URLSearchParams(params).toString();
    }
    // 否则使用循环
    var pairs = [];
    each(params, function (key, value) {
        pairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    });
    return pairs.join('&');
}
/**
 * 如果对象值是函数，返回函数调用计算完毕后的值对象
 * @param hash 参数对象
 * @returns object
 */
export function evaluateHash(hash) {
    var evaluated = {};
    for (var key in hash) {
        if (hash.hasOwnProperty(key)) {
            var value = hash[key];
            evaluated[key] = isFunction(value) ? value() : value;
        }
    }
    return evaluated;
}
/**
 * 序列化消息 Keys，最长 maxLength 个字符，超出末尾添加 ...
 * @param keys 键列表
 * @param maxLength 最大长度
 * @returns string
 */
export function serializeKeysForMessage(keys, maxLength) {
    if (maxLength === void 0) { maxLength = MAX_SERIALIZE_KEYS_LENGTH; }
    if (isNumber(keys) || isString(keys)) {
        return keys.toString();
    }
    if (!isArray(keys)) {
        return '';
    }
    keys = keys.filter(function (key) { return isString(key); });
    if (keys.length === 0) {
        return '[object has no keys]';
    }
    maxLength = isNumber(maxLength) ? maxLength : MAX_SERIALIZE_KEYS_LENGTH;
    // 如果第一个字符串已经超出长度，直接返回第一个字符串
    if (keys[0].length >= maxLength) {
        return keys[0];
    }
    // 首先取所有键，如果拼接长度没有超出，返回，否则逐步减少键个数
    for (var usedKeys = keys.length; usedKeys > 0; usedKeys--) {
        var serialized = keys.slice(0, usedKeys).join(', ');
        // 过长了，减去最后一个值，重新遍历
        if (serialized.length > maxLength) {
            continue;
        }
        // 第一次循环就满足，直接返回即可
        if (usedKeys === keys.length) {
            return serialized;
        }
        // 否则加上 ... 符号
        return serialized + '\u2026';
    }
    return '';
}
/**
 * 递归序列化对象/数组，避免 JSON 直接序列化会丢失函数、正则等问题
 * @param value 对象，可能为数组
 * @param depth 序列化的深度，如果为0，对象直接序列化为 '[Object]'，数组直接序列化为 '[Array]' 等
 * @returns string | object，如果为0返回字符串，否则返回对象
 */
export function serializeObject(value, depth) {
    if (depth === 0) {
        return serializeValue(value);
    }
    if (isPlainObject(value)) {
        return Object.keys(value).reduce(function (acc, key) {
            acc[key] = serializeObject(value[key], depth - 1);
            return acc;
        }, {});
    }
    else if (Array.isArray(value)) {
        return value.map(function (val) {
            return serializeObject(val, depth - 1);
        });
    }
    return serializeValue(value);
}
/**
 * 序列化异常信息
 * @param ex 异常对象
 * @param depth 对象层级深度
 * @param maxSize 最大长度
 * @returns string | object，非对象直接返回字符串，否则返回处理后的对象
 */
export function serializeException(ex, depth, maxSize) {
    if (depth === void 0) { depth = MAX_SERIALIZE_EXCEPTION_DEPTH; }
    if (maxSize === void 0) { maxSize = MAX_SERIALIZE_EXCEPTION_SIZE; }
    // 非对象，直接返回
    if (!isPlainObject(ex)) {
        return ex + '';
    }
    var serialized = serializeObject(ex, depth);
    // 如果异常信息超长，递归减少一层
    if (jsonSize(stringify(serialized)) > maxSize) {
        return serializeException(ex, depth - 1);
    }
    return serialized;
}
/**
 * 序列化值为字符串
 * @param value 待序列化的值
 * @returns string
 */
export function serializeValue(value) {
    if (isString(value)) {
        var maxLength = 40;
        return truncate(value, maxLength);
    }
    else if (isNumber(value) || isBoolean(value) || isUndefined(value)) {
        return value + '';
    }
    // Node.js REPL notation
    if (isPlainObject(value)) {
        return '[Object]';
    }
    if (isArray(value)) {
        return '[Array]';
    }
    if (isFunction(value)) {
        return value.name ? '[Function: ' + value.name + ']' : '[Function]';
    }
    return value;
}
/**
 * 获取对象 JSON.stringify 后的字节长度，会带上两个双引号的长度
 * 例如 jsonSize('abc') 会返回 5，因为序列化后的字符串为 "abc"，jsonSize('你好!') 返回 9
 * @param value 对象
 * @returns JSON.stringify 后的长度
 */
export function jsonSize(value) {
    return utf8Length(JSON.stringify(value));
}
/**
 * 获取字符串utf8字节长度
 * @param str 待获取长度的字符串
 * @returns 字符串utf8字节长度
 */
export function utf8Length(str) {
    return ~-encodeURI(str).split(/%..|./).length;
}
/**
 * 字符串截断，超出长度添加 '...'，因此如果截断后，长度为 max + 1
 * @param str 待截断字符串
 * @param max 待截断的位数
 * @returns string
 */
export function truncate(str, max) {
    if (max === 0) {
        return str;
    }
    return str.length <= max ? str : str.substr(0, max) + '\u2026';
}
// borrowed from https://tools.ietf.org/html/rfc3986#appendix-B
// intentionally using regex and not <a/> href parsing trick because React Native and other
// environments where DOM might not be available
// TODO: 为什么不使用 parse-url 库
export function parseUrl(url) {
    var defaultObj = {
        protocol: '',
        host: '',
        path: '',
        relative: ''
    };
    if (typeof url !== 'string') {
        return defaultObj;
    }
    var match = url.match(/^(([^:\/?#]+):)?(\/\/([^\/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?$/);
    // coerce to undefined values to empty string so we don't get 'undefined'
    if (match && match.length) {
        var query = match[6] || '';
        var fragment = match[8] || '';
        return {
            protocol: match[2] || '',
            host: match[4] || '',
            path: match[5] || '',
            relative: (match[5] || '') + query + fragment // everything minus origin
        };
    }
    return defaultObj;
}
/**
 * Given a child DOM element, returns a query-selector statement describing that
 * and its ancestors
 * e.g. [HTMLElement] => body > div > input#foo.btn[name=baz]
 * @param elem
 * @returns 元素路径字符串，递归上级节点，进行拼接
 */
export function htmlTreeAsString(elem) {
    /* eslint no-extra-parens:0*/
    var MAX_TRAVERSE_HEIGHT = 5;
    var MAX_OUTPUT_LEN = 80;
    var out = [];
    var separator = ' > ';
    var sepLength = separator.length;
    var nextStr;
    var height = 0;
    var len = 0;
    while (elem && height++ < MAX_TRAVERSE_HEIGHT) {
        nextStr = htmlElementAsString(elem);
        // 如果到达 html 节点，或者到达长度限制，退出
        var isHTMLNode = nextStr === 'html';
        var reachMaxOutputLen = height > 1 && len + out.length * sepLength + nextStr.length >= MAX_OUTPUT_LEN;
        if (isHTMLNode || reachMaxOutputLen) {
            break;
        }
        out.push(nextStr);
        len += nextStr.length;
        elem = elem.parentNode;
    }
    return out.reverse().join(separator);
}
/**
 * Returns a simple, query-selector representation of a DOM element
 * e.g. [HTMLElement] => input#foo.btn[name=baz]
 * @param el HTMLElement
 * @returns 元素路径字符串
 */
export function htmlElementAsString(el) {
    var e_1, _a, e_2, _b;
    if (!el || !el.tagName) {
        return '';
    }
    var out = [el.tagName.toLowerCase()];
    if (el.id) {
        out.push('#' + el.id);
    }
    var classes = el.className ? el.className.split(/\s+/) : [];
    try {
        for (var classes_1 = __values(classes), classes_1_1 = classes_1.next(); !classes_1_1.done; classes_1_1 = classes_1.next()) {
            var cls = classes_1_1.value;
            out.push('.' + cls);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (classes_1_1 && !classes_1_1.done && (_a = classes_1.return)) _a.call(classes_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var attrWhitelist = ['type', 'name', 'title', 'alt'];
    try {
        for (var attrWhitelist_1 = __values(attrWhitelist), attrWhitelist_1_1 = attrWhitelist_1.next(); !attrWhitelist_1_1.done; attrWhitelist_1_1 = attrWhitelist_1.next()) {
            var k = attrWhitelist_1_1.value;
            var v = el.getAttribute(k);
            if (v) {
                out.push('[' + k + '=' + v + ']');
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (attrWhitelist_1_1 && !attrWhitelist_1_1.done && (_b = attrWhitelist_1.return)) _b.call(attrWhitelist_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return out.join('');
}
/**
 * 检测两个堆栈是否相同，算法如下：
 * 1. 长度比较
 * 2. 每一帧比较
 */
export function isSameStacktrace(stack1, stack2) {
    if (hasArrayValue(stack1) && hasArrayValue(stack2)) {
        var l1 = stack1.length;
        var l2 = stack2.length;
        if (l1 !== l2) {
            return false;
        }
        for (var i = 0; i < l1; i++) {
            var s1 = stack1[i];
            var s2 = stack2[i];
            if (!compareFrame(s1, s2)) {
                return false;
            }
        }
        return true;
    }
    return false;
}
/**
 * 检测两个异常是否相同，算法如下：
 * 1. 检测 mechanism、type、value、url
 * 2. 检测两个堆栈是否相同
 */
export function isSameException(e1, e2) {
    // 如果值不同，直接返回 false
    if (e1.mechanism !== e2.mechanism || e1.type !== e2.type || e1.value !== e2.value || e1.url !== e2.url) {
        return false;
    }
    var allStackEmpty = e1.stacktrace.length === 0 && e2.stacktrace.length === 0;
    // 如果堆栈全部为空 []，那么属于同一个异常
    return allStackEmpty ? true : isSameStacktrace(e1.stacktrace, e2.stacktrace);
}
/**
 * 获取异常的字符串表现形式，后续根据该字符串计算 md5，获取 event id
 * @param 负载参数
 * @returns 异常的字符串表现形式
 */
export function getExceptionString(payload) {
    var ex = payload.exception;
    var exDigest = [];
    exDigest.push('token=' + ex.token); // 项目
    exDigest.push('type=' + ex.type); // 类型
    exDigest.push('mechanism=' + ex.mechanism); // 异常捕获方式
    exDigest.push('value=' + ex.value); // 消息
    exDigest.push('url=' + ex.url); // 来源 URL
    if (ex.stacktrace) {
        ex.stacktrace.forEach(function (s) {
            exDigest.push('filename=' + s.filename || '');
            exDigest.push('lineno=' + s.lineno || '');
            // 这里在不同的浏览器中也会有微小差异，例如 148366 和 148377
            // 奇怪的是两者计算出来的原始行列是一样的。
            exDigest.push('colno=' + s.colno || '');
            // 有时候会不一致，例如：
            // Object.C.e.Qb.t[as updateDirectives] 和 Object.updateDirectives
            // e.a[as_next] 和 e.object.a[as _next]
            // 因此考虑对比异常时，将函数排除名称
            // exDigest.push('function=' + s['function'] || '');
        });
    }
    return exDigest.join('***');
}
/**
 * 获取异常的字符串表现形式，后续根据该字符串计算 md5，获取 data id
 * 与 getExceptionString 不同的是，这里会计算环境信息
 * @param 负载参数
 * @returns 异常的字符串表现形式
 */
export function getAllExceptionString(payload) {
    // 基本信息
    var basicInfo = getExceptionString(payload);
    var exDigest = [];
    // 环境信息
    var request = payload.request, env = payload.env, behaviors = payload.behaviors;
    // 某些情况下，暂时无法复现，request 会为 undefined
    // 但是在 _trimPacket 函数中会保证这个函数存在，所以比较奇怪，这里做下兼容处理
    if (!request) {
        return JSON.stringify(payload);
    }
    var headers = request.headers, url = request.url;
    exDigest.push('Referrer=' + headers.Referrer);
    exDigest.push('ua=' + headers['User-Agent']);
    exDigest.push('url=' + url);
    each(env, function (k, v) {
        if (k !== 'session:duration') {
            exDigest.push(k + "=" + v);
        }
    });
    each(behaviors, function (behavior) {
        exDigest.push("behavior.category=" + behavior.category);
        exDigest.push("behavior.name=" + behavior.name);
        exDigest.push("behavior.message=" + behavior.message);
    });
    var envInfo = exDigest.join('***');
    return basicInfo + envInfo;
}
/**
 * 数组是否有值
 * @param v 待检测值
 * @returns 是否是数组并且有值
 */
function hasArrayValue(v) {
    return v && Array.isArray(v) && v.length > 0;
}
/**
 * 比较两帧，如果两帧文件名、函数、行、列均相同，则视为同一帧
 * @returns 两帧是否相同
 */
function compareFrame(f1, f2) {
    return (f1.filename === f2.filename &&
        f1.lineno === f2.lineno &&
        f1.colno === f2.colno &&
        f1['function'] === f2['function']);
}
/**
 * 将多个字符串或者正则表达式拼接成一个大的正则表达式
 * @param patterns 正则表达式或字符串数组，可混用
 * @returns 大的正则表达式
 */
export function joinRegExp(patterns) {
    // Combine an array of regular expressions and strings into one large regexp
    // Be mad.
    var sources = [];
    var len = patterns.length;
    for (var i = 0; i < len; i++) {
        var pattern = patterns[i];
        if (isString(pattern)) {
            // If it's a string, we need to escape it
            // Taken from: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
            sources.push(pattern.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1'));
        }
        else if (pattern && pattern.source) {
            // If it's a regexp already, we want to extract the source
            sources.push(pattern.source);
        }
    }
    return new RegExp(sources.join('|'), 'i');
}
/**
 * 对输入数据对象中的某些 key 对应的值进行打码操作
 * @param input 输入数据对象
 * @param sanitizeKeys 过滤的 keys
 * @returns 打码后的对象或者数组
 */
export function sanitize(input, sanitizeKeys) {
    if (!Array.isArray(sanitizeKeys) || (Array.isArray(sanitizeKeys) && sanitizeKeys.length === 0)) {
        return input;
    }
    var sanitizeRegExp = joinRegExp(sanitizeKeys);
    var sanitizeMask = '********';
    var safeInput;
    try {
        // 复制一个对象
        safeInput = JSON.parse(stringify(input));
    }
    catch (o_O) {
        return input;
    }
    function sanitizeWorker(workerInput) {
        if (Array.isArray(workerInput)) {
            return workerInput.map(function (val) {
                return sanitizeWorker(val);
            });
        }
        if (isPlainObject(workerInput)) {
            return Object.keys(workerInput).reduce(function (acc, k) {
                if (sanitizeRegExp.test(k)) {
                    acc[k] = sanitizeMask;
                }
                else {
                    acc[k] = sanitizeWorker(workerInput[k]);
                }
                return acc;
            }, {});
        }
        return workerInput;
    }
    return sanitizeWorker(safeInput);
}
/**
 * 替换一些正则表达式的转移字符，便于在 new RegExp 中使用
 * @param str 原始字符串
 * @return 转义过的字符串
 */
export function regexStringTransform(str) {
    return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1');
}
/**
 * 检测 message 是否符合某个正则或者字符串数组
 * @param regexArray 正则或者字符串数组
 * @param message 待检测字符串
 * @returns 检测结果
 */
export function multipleTest(regexArray, message) {
    if (!isString(message)) {
        return false;
    }
    // 如果某个正则符合了就返回true
    return regexArray.some(function (r) {
        // 空校验
        if (!r) {
            return false;
        }
        if (isString(r)) {
            return message.indexOf(r) !== -1;
        }
        if (isRegExp(r)) {
            try {
                return r.test(message);
            }
            catch (e) {
                console.error(r + "\u6B63\u5219\u4E66\u5199\u6709\u8BEF");
                return false;
            }
        }
        console.error(r + "\u5FFD\u7565\u5339\u914D\u9879\u7C7B\u578B\u5E94\u8BE5\u662FString\u3001RegExp");
        return false;
    });
}
/** 计算 API 异常 ISSUE ID */
export function computeAPIExceptionIssueId(api) {
    return md5(api.method + '***' + formatURL(api.url));
}
/** 计算 API 异常事件 ID */
export function computeAPIExceptionDataId(apiData) {
    return md5(stringify(apiData));
}
export function transformResponse(responseType, response) {
    if (responseType === void 0) { responseType = ''; }
    if (response === void 0) { response = ''; }
    var strictJSONParsing = responseType === '' || responseType === 'text';
    if (strictJSONParsing && isString(response) && response.length) {
        try {
            return JSON.parse(response);
        }
        catch (e) {
            return response;
        }
    }
    if (responseType === 'json') {
        return JSON.parse(JSON.stringify(response));
    }
    // 对blob、ms-stream、arraybuffer、document 等暂不处理
    return '';
}
/**
 * 解析 URL 字段
 * - 模糊queryParams数字和中文
 * @param { string } URL字符串
 */
export function formatURL(rawURL) {
    if (!isString(rawURL)) {
        return '';
    }
    var urlArr = rawURL.split('?');
    if (urlArr[1]) {
        urlArr[1] = urlArr[1].replace(/\d+/g, '*').replace(/[\u4e00-\u9fa5]+/gi, '*');
        return urlArr.join('?');
    }
    return rawURL;
}
/**
 * 格式化截取用户行为数据
 * - 转成字符串截取
 * @param { string } 需要处理的数据
 */
export function formatBehaviorData(data) {
    var MAX_BEHAVIOR_LENGTH = 256;
    var result = isString(data) ? data : JSON.stringify(data);
    return result.slice(0, MAX_BEHAVIOR_LENGTH);
}
//# sourceMappingURL=misc.js.map